import {
  Color,
  type AnimationClip,
  type Mesh,
  type MeshStandardMaterial,
  type Object3D,
} from 'three'
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import type { CharacterKind, PropKind } from '@/components'

/**
 * Optional external content.
 *
 * Version 1.0 generates every mesh in code, but the pipeline for real models is
 * here and working rather than promised: drop a `.glb` next to `index.html`,
 * load it, register it against a prop kind, and every future spawn of that prop
 * uses the model instead of the procedural mesh. Nothing in the simulation
 * changes — it only ever deals with collider numbers and an object id.
 *
 *   const assets = new AssetLibrary()
 *   await assets.registerProp('car', '/models/uaz.glb', { scale: 1.2 })
 *
 * Models are stored as templates and cloned per instance, so a hundred crates
 * cost one parse.
 */
export interface ModelOptions {
  /** Uniform scale applied to the template once, at registration time. */
  scale?: number
  /** Vertical offset, for models whose origin is not at ground level. */
  yOffset?: number
  /** Yaw correction, for models authored facing a different axis. */
  yawOffset?: number
}

/** Materials whose colour is part of the character, not of the player slot. */
const KEEP_COLOUR = /^(eye|teeth|tongue|skin|hair|eyebrow)/i

export class AssetLibrary {
  private readonly loader = new GLTFLoader()
  private readonly propModels = new Map<PropKind, Object3D>()
  private readonly fighterModels = new Map<CharacterKind, Object3D>()
  private readonly fighterClips = new Map<CharacterKind, AnimationClip[]>()
  private readonly fighterFitted = new Set<CharacterKind>()

  /** Loads a glTF/glb and returns its scene root. */
  async load(url: string): Promise<Object3D> {
    const gltf = await this.loader.loadAsync(url)
    return gltf.scene
  }

  /**
   * Loads a model and makes it the visual for a prop kind. Colliders are
   * unaffected: physics keeps using the profile in `game/prefabs.ts`, so a
   * swapped model never silently changes how the game plays.
   */
  async registerProp(
    kind: PropKind,
    url: string,
    options: ModelOptions = {},
  ): Promise<void> {
    const root = await this.load(url)
    if (options.scale !== undefined) root.scale.setScalar(options.scale)
    if (options.yOffset !== undefined) root.position.y = options.yOffset
    if (options.yawOffset !== undefined) root.rotation.y = options.yawOffset

    root.traverse((node) => {
      node.castShadow = true
      node.receiveShadow = true
    })
    this.propModels.set(kind, root)
  }

  /** A fresh clone for one spawn, or null when the kind has no model. */
  instantiateProp(kind: PropKind): Object3D | null {
    const template = this.propModels.get(kind)
    return template ? template.clone(true) : null
  }

  /**
   * Substitutes a modelled fighter for the procedural rig.
   *
   * Everything in this game is generated in code, and for the arena, the props
   * and the effects that is the right trade. For the *fighters* it is a ceiling:
   * a character assembled from spheres and capsules looks assembled however it
   * is arranged, and three redesigns hit that wall in a row. This is the door
   * out — drop a `.glb` in and the rig steps aside.
   *
   * The model replaces the visual only. The collider, the kit and every number
   * that decides a fight stay where they are, so importing art can never change
   * the balance.
   *
   * The procedural animation does not drive an imported skeleton: a model
   * registered here is posed by position and facing alone. Anything richer
   * needs its own clips, which is a different feature.
   */
  async registerFighter(
    kind: CharacterKind,
    url: string,
    options: ModelOptions = {},
    /** Bytes of this model received so far, as they arrive. */
    onProgress?: (loaded: number) => void,
  ): Promise<void> {
    const gltf = await this.loader.loadAsync(url, onProgress && ((event) => onProgress(event.loaded)))
    const root = gltf.scene
    if (options.scale !== undefined) root.scale.setScalar(options.scale)
    if (options.yOffset !== undefined) root.position.y = options.yOffset
    if (options.yawOffset !== undefined) root.rotation.y = options.yawOffset
    root.traverse((node) => {
      node.castShadow = true
      node.receiveShadow = true
    })
    this.fighterModels.set(kind, root)
    // An explicit scale means the caller measured the model properly; the
    // renderer must not then second-guess it with an automatic fit.
    if (options.scale !== undefined) this.fighterFitted.add(kind)
    // The clips travel with the model: a character that stands still is not an
    // upgrade over the procedural rig, it is a statue with better topology.
    this.fighterClips.set(kind, gltf.animations ?? [])
  }

  /** True when the model came with a measured scale and needs no auto-fit. */
  fighterIsFitted(kind: CharacterKind): boolean {
    return this.fighterFitted.has(kind)
  }

  /** Clips that came with the model, for the renderer's mixer. */
  fighterClips_(kind: CharacterKind): AnimationClip[] {
    return this.fighterClips.get(kind) ?? []
  }

  /**
   * A clone of the registered model, tinted to the player's colour.
   *
   * Recolouring is not optional here: on a shared screen the colour of a
   * fighter is how you know which one is yours, and an imported model arrives
   * with whatever palette its author chose. Materials are cloned per instance
   * before tinting, or six fighters would share one material and one colour.
   *
   * The tint is a multiply toward the slot colour rather than a replacement, so
   * the model keeps its own shading and any texture detail it came with.
   */
  instantiateFighter(kind: CharacterKind, color: number): Object3D | null {
    const template = this.fighterModels.get(kind)
    if (!template) return null

    // SkeletonUtils.clone, not Object3D.clone: a plain clone of a skinned
    // mesh keeps pointing at the original's bones, so every copy would animate
    // in lockstep with the first one.
    const clone = cloneSkinned(template)
    const tint = new Color(color)
    clone.traverse((node) => {
      const mesh = node as Mesh
      // A skinned mesh's bounding sphere is computed in bind pose, so once the
      // model is scaled and animated three culls it against a volume that no
      // longer has anything to do with where it actually is — the fighter
      // simply stops being drawn.
      node.frustumCulled = false
      if (!mesh.material) return
      const list = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      mesh.material = list.map((source) => {
        const material = source.clone() as MeshStandardMaterial
        // Eyes, teeth and tongues keep their own colour. Tinting everything
        // painted the whites of the eyes the player's colour and the face
        // stopped being a face — and the face is most of what tells six
        // fighters apart at this size.
        if (!KEEP_COLOUR.test(material.name ?? '')) {
          if (material.color) material.color.lerp(tint, 0.55)
                    // 0.12 was chosen when the fighters were untextured primitives. On a
          // model it feeds the bloom pass until the whole body is one glowing
          // lozenge and the silhouette — the helmet, the hat, the mohawk — is
          // gone at gameplay distance. Enough tint to read the slot, not
          // enough to erase the character.
          if (material.emissive) material.emissive.copy(tint).multiplyScalar(0.05)
        }
        return material
      })
      if (!Array.isArray(mesh.material)) return
      if (mesh.material.length === 1) mesh.material = mesh.material[0]
    })
    return clone
  }

  get hasOverrides(): boolean {
    return this.propModels.size > 0 || this.fighterModels.size > 0
  }
}
